import { LESSON_NAMES } from 'constants/lessons'

// Define data structure for pros cards
interface ProCardData {
  id: number
  icon: string
  title: string
  description: string
  actionText: string
  actionLink: string
  readMoreLink?: string
  lesson?: LESSON_NAMES
}

// Define data structure for feature cards
interface FeatureCardData {
  id: number
  icon: string
  title: string
  description: string
}

// data for feature cards
export const featureCardsData: FeatureCardData[] = [
  {
    id: 1,
    icon: '⏱️',
    title: 'Track Your Progress',
    description: 'Monitor your typing speed, accuracy, and improvement over time with detailed analytics.',
  },
  {
    id: 2,
    icon: '🔍',
    title: 'Identify Weaknesses',
    description: 'Our system highlights problem keys and patterns to focus your practice effectively.',
  },
  {
    id: 3,
    icon: '📊',
    title: 'Personalized Practice',
    description: 'Get custom exercises designed to target your specific areas for improvement.',
  },
  {
    id: 4,
    icon: '💡',
    title: 'AI-Powered Coaching',
    description:
      'Unlock your full typing potential with our AI-powered typing coach that adapts to your individual needs',
  },
]

// data for pros cards
export const prosCardsData: ProCardData[] = [
  {
    id: 1,
    icon: '🌐',
    title: '1. Online Typing Practice',
    description:
      'Accurate data about your progression, identify problem areas, and access to a variety of texts. Much better than offline practice with organized features to help you achieve good typing speed in a short time. Ideal for Beginners, Remote Workers, and Data Entry Operators, Freelancers.',
    actionText: 'Start Typing',
    actionLink: '/typing-practice/',
    readMoreLink: '/articles/online-typing-practice',
    lesson: LESSON_NAMES.PARAGRAPH,
  },
  {
    id: 2,
    icon: '📝',
    title: '2. Typing Practice Paragraphs',
    description:
      'Type meaningful paragraphs that reflect real-world content instead of random characters. This practice helps you write emails, reports and assignments faster and more efficiently. Ideal for Intermediate Typists, Students, Business Professionals, and Bloggers, Script Writers.',
    actionText: 'Practice Paragraph',
    actionLink: '/typing-practice/',
    lesson: LESSON_NAMES.PARAGRAPH,
    readMoreLink: '/articles/typing-practice-paragraphs/',
  },
  {
    id: 3,
    icon: '👉',
    title: '3. Right Hand Typing Practice',
    description:
      'Most of the keyboard characters are under your right hand. Specialized training helps develop proper technique and accuracy for keys operated by your right hand. Ideal for Advanced Typists, Programmers, Secretaries, and Competitive Typists.',
    actionText: 'Train Right Hand',
    actionLink: '/typing-practice/',
    lesson: LESSON_NAMES.H_RIGHT,
    readMoreLink: '/articles/right-hand-typing-practice/',
  },
  {
    id: 4,
    icon: '👈',
    title: '4. Left Hand Typing Practice',
    description:
      'Left hand fingers cover fewer characters but are equally important. Regular practice prevents your overall typing speed from being limited by your left hand performance. Ideal for Advanced Typists, Copywriters, Journalists, and Content Creators.',
    actionText: 'Train Left Hand',
    actionLink: '/typing-practice/',
    lesson: LESSON_NAMES.H_LEFT,
    readMoreLink: '/articles/left-hand-typing-practice/',
  },
  {
    id: 5,
    icon: '🔢',
    title: '5. Number Typing Practice',
    description:
      'Many typists struggle with numeric characters. If your work requires frequent number typing, specialized practice can significantly boost your overall efficiency and speed. Ideal for Advanced typists, Accountants, Data Entry Scientists, and Finance Professionals.',
    actionText: 'Practice Numbers',
    actionLink: '/typing-practice/',
    lesson: LESSON_NAMES.NUM,
    readMoreLink: '/articles/number-typing-practice/',
  },
  {
    id: 6,
    icon: '💬',
    title: '6. Typing Practice Quotes',
    description:
      'Make typing practice fun with motivational quotes. These exercises not only improve your skills but also have a positive impact on your mood and mindset. Ideal for Intermediate Typists, Type Racers, Enthusiasts, and Motivational Writers.',
    actionText: 'Start Typing Quotes',
    actionLink: '/typing-practice/',
    lesson: LESSON_NAMES.QUOTES,
    readMoreLink: '/articles/typing-practice-quotes/',
  },
  {
    id: 7,
    icon: '👶',
    title: '7. Typing Practice For Kids Works Best',
    description:
      'Children learn touch typing quickly. This valuable skill gives them an advantage throughout their educational and professional lives, expanding their thought processes as they type. Ideal for Beginners, School Students, Early Learners, and Young Typists.',
    actionText: 'Start Learning',
    actionLink: '/typing-practice/',
    lesson: LESSON_NAMES.PARAGRAPH,
  },
  {
    id: 8,
    icon: '👨‍💼',
    title: '8. Typing Practice For Adults',
    description:
      'It&apos;s never too late to learn touch typing. This skill won&apos;t take much time to acquire but will benefit you for life, making all computer-based tasks faster and more efficient. Ideal for Adult Beginners, Career Professionals, Job Seekers, and Adult Learners.',
    actionText: 'Practice for Adults',
    actionLink: '/typing-practice/',
    lesson: LESSON_NAMES.PARAGRAPH,
  },
]

export const TypingCoachFeatureItems: string[] = [
  'Adaptive learning tailored to your skill level',
  'Personalized feedback on your performance',
  'Dynamic practice texts based on your interests',
  'Real-time progress tracking with analytics',
  'Specialized typing drills',
  'Continuous difficulty adjustment as you improve',
]
